/**
 * Help FAB Component
 *
 * Vertical tab docked to the right side that opens the help panel.
 * Shows a small indicator when context-aware help is available.
 */

import { useState } from 'react';
import { useHelpContext } from './HelpContext';
import { HelpPanel } from './HelpPanel';

interface HelpFABProps {
  /** Distance from top of viewport */
  topOffset?: number;
  /** Z-index for the FAB */
  zIndex?: number;
}

export function HelpFAB({ topOffset = 200, zIndex = 9990 }: HelpFABProps) {
  const { togglePanel, isOpen, contextDocs } = useHelpContext();
  const [isHovered, setIsHovered] = useState(false);

  const hasContextHelp = contextDocs.length > 0;

  return (
    <>
      <button
        onClick={togglePanel}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="Open help panel"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        style={{
          position: 'fixed',
          top: topOffset,
          right: 0,
          width: '40px',
          height: '100px',
          borderRadius: '8px 0 0 8px',
          backgroundColor: isOpen
            ? 'var(--pm-cyan-bg-strong)'
            : isHovered
            ? 'var(--pm-cyan-bg-strong)'
            : 'var(--pm-bg-card)',
          border: '1px solid var(--pm-cyan-border)',
          borderRight: 'none',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          backdropFilter: 'blur(8px)',
          transition: 'background-color 0.2s, transform 0.2s',
          transform: isHovered ? 'translateX(-2px)' : 'translateX(0)',
          zIndex,
        }}
      >
        {/* Vertical "Help" text */}
        <span
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)',
            fontSize: '13px',
            fontWeight: 500,
            letterSpacing: '0.05em',
            color: 'var(--pm-cyan-text)',
          }}
        >
          Help
        </span>

        {/* Help Circle Icon */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--pm-cyan-text)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <path d="M12 17h.01" />
        </svg>

        {/* Context help indicator dot */}
        {hasContextHelp && !isOpen && (
          <span
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: 'var(--pm-star-active)',
              animation: 'help-pulse 2s infinite',
            }}
          />
        )}
      </button>

      <HelpPanel />

      <style>
        {`
          @keyframes help-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.1); }
          }
        `}
      </style>
    </>
  );
}
